import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { 
  Container, 
  Typography, 
  Button, 
  TextField, 
  Grid, 
  Paper, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow,
  Snackbar,
  Alert,
  Box
} from '@mui/material';
import baseURL from '../../baseURL';
import AdminNavbar from '../admin/AdminNavbar';
// Predefined functional keys
const PREDEFINED_FUNCTIONAL_KEYS = [
  { keyName: 'F1', keyNumber: 1, defaultPercentage: 18 },
  { keyName: 'F2', keyNumber: 2, defaultPercentage: 20 },
  { keyName: 'F3', keyNumber: 3, defaultPercentage: 24 },
  { keyName: 'F4', keyNumber: 4, defaultPercentage: 26 },
  { keyName: 'F5', keyNumber: 5, defaultPercentage: 28 }
];

const FunctionalKeysPage = () => {
  // State for functional keys
  const [functionalKeys, setFunctionalKeys] = useState(PREDEFINED_FUNCTIONAL_KEYS);
  
  // State for percentages
  const [percentages, setPercentages] = useState(
    PREDEFINED_FUNCTIONAL_KEYS.reduce((acc, key) => {
      acc[key.keyNumber] = key.defaultPercentage;
      return acc;
    }, {})
  );
  
  // State for error handling
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Initialize all functional keys
  const initializeFunctionalKeys = async () => {
    try {
      const token = localStorage.getItem('token');
      
      // Create each predefined key
      for (let key of PREDEFINED_FUNCTIONAL_KEYS) {
        await axios.post(`${baseURL}/functional-keys/create`, 
          { 
            keyName: key.keyName, 
            keyNumber: key.keyNumber, 
            percentage: key.defaultPercentage 
          },
          {
            headers: { 
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          }
        );
      }

      setSuccess('All functional keys initialized successfully');
      fetchFunctionalKeys();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to initialize functional keys');
    }
  };

  // Fetch functional keys
  const fetchFunctionalKeys = async () => {
    try {
      // Get token from local storage
      const token = localStorage.getItem('token');
      
      const response = await axios.get(`${baseURL}/functional-keys`, {
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      // If keys exist in backend, update percentages
      if (response.data.data.functionalKeys.length > 0) {
        const backendKeys = response.data.data.functionalKeys;
        const newPercentages = backendKeys.reduce((acc, key) => {
          acc[key.keyNumber] = key.percentage;
          return acc;
        }, {});
        setPercentages(newPercentages);
      }
    } catch (err) {
      // If no keys exist, use default keys
      setError(err.response?.data?.message || 'Failed to fetch functional keys');
    }
  };

  // Update functional key percentage
  const updateFunctionalKeyPercentage = async (keyNumber, newPercentage) => {
    try {
      const token = localStorage.getItem('token');
      
      const selectedKey = functionalKeys.find(k => k.keyNumber === keyNumber);
      
      // Use PATCH method with the correct endpoint
      await axios.patch(`${baseURL}/functional-keys/update-percentage`, 
        { 
          keyName: selectedKey.keyName,
          keyNumber: keyNumber,
          percentage: newPercentage 
        },
        {
          headers: { 
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      // Update local state
      setPercentages(prev => ({
        ...prev,
        [keyNumber]: newPercentage
      }));

      setSuccess(`${selectedKey.keyName} percentage updated successfully`);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to update functional key percentage');
    }
  };

  // Load functional keys on component mount
  useEffect(() => {
    fetchFunctionalKeys();
  }, []);

  // Close error/success messages
  const handleCloseMessage = () => {
    setError(null);
    setSuccess(null);
  };

  return (
    <Container maxWidth="md">
      <AdminNavbar />
      {/* Error/Success Notifications */}
      <Snackbar 
        open={!!error} 
        autoHideDuration={6000} 
        onClose={handleCloseMessage}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseMessage} severity="error" sx={{ width: '100%' }}>
          {error}
        </Alert>
      </Snackbar>

      <Snackbar 
        open={!!success} 
        autoHideDuration={6000} 
        onClose={handleCloseMessage}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseMessage} severity="success" sx={{ width: '100%' }}>
          {success}
        </Alert>
      </Snackbar>

      <Typography variant="h4" gutterBottom>
        Functional Keys Management
      </Typography>

      {/* Initialize Keys Button */}
      <Box sx={{ mb: 2 }}>
        <Button 
          variant="contained" 
          color="primary" 
          onClick={initializeFunctionalKeys}
        >
          Initialize Functional Keys
        </Button>
      </Box>

      {/* Functional Keys Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Key Name</TableCell>
              <TableCell>Percentage</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {functionalKeys.map((key) => (
              <TableRow key={key.keyNumber}>
                <TableCell>{key.keyName}</TableCell>
                <TableCell>
                  <TextField
                    type="number"
                    value={percentages[key.keyNumber] || 0}
                    onChange={(e) => {
                      const newPercentage = e.target.value;
                      setPercentages(prev => ({
                        ...prev,
                        [key.keyNumber]: newPercentage
                      }));
                    }}
                    inputProps={{ min: 0, max: 100 }}
                    sx={{ width: 100 }}
                  />
                </TableCell>
                <TableCell>
                  <Button 
                    variant="outlined" 
                    color="primary"
                    onClick={() => updateFunctionalKeyPercentage(key.keyNumber, percentages[key.keyNumber])}
                  >
                    Update
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Container>
  );
};

export { PREDEFINED_FUNCTIONAL_KEYS };
export default FunctionalKeysPage;